import { SwanStyleSheetKey } from "@vp/swan";
import React from "react";
import {
  ComponentToBeRegistered,
  Placeholder,
  RegisteredComponent,
  PlaceholderPlugin,
  PluginDataNeeds,
  PluginEnableContext,
  PluginExperimentContext,
  PluginStateDefinition,
  PlaceholderGroupMember,
} from "./PluginRegistryTypes";

export default class PluginRegistry {
  private componentRegistry: {
    [key in Placeholder | PlaceholderGroupMember]?: RegisteredComponent<any>[];
  } = {};
  private dataNeeds: PluginDataNeeds = { contentful: {} };
  private styleNeeds: SwanStyleSheetKey[] = [];
  private stateDefinitions: PluginStateDefinition[] = [];

  constructor() {
    for (const placeholder in Placeholder) {
      this.componentRegistry[placeholder as Placeholder] = [];
    }

    for (const placeholder in PlaceholderGroupMember) {
      this.componentRegistry[placeholder as PlaceholderGroupMember] = [];
    }
  }

  public getComponentRegistry() {
    return { ...this.componentRegistry };
  }

  public getAllDataNeeds() {
    return { ...this.dataNeeds };
  }

  public getAllStyleNeeds() {
    return [...this.styleNeeds];
  }

  public getAllStateDefinitions() {
    return [...this.stateDefinitions];
  }

  public addComponent<T>(plugin: PlaceholderPlugin, component: ComponentToBeRegistered<T>) {
    const registeredComponent: RegisteredComponent<T> = {
      ...component,
      ...plugin,
    };

    if (!this.componentRegistry[component.placeholder]) {
      this.componentRegistry[component.placeholder] = [];
    }
    this.componentRegistry[component.placeholder]!.push(registeredComponent);

    this.addDataNeeds(component.name, component.dataNeeds);
    this.addStyleNeeds(component.styleNeeds);
  }

  public addContextProvider(context: PluginStateDefinition) {
    const isExistingContext = this.stateDefinitions.find(
      stateDefinition => stateDefinition.name === context.name
    );
    if (!isExistingContext) {
      this.stateDefinitions.push(context);
    }
  }

  public getComponents(
    useExperiment: (context: PluginExperimentContext) => string | undefined,
    placeholder: Placeholder | PlaceholderGroupMember,
    context: PluginEnableContext,
    parentComponents: string[],
    orderedComponents?: string[][]
  ): RegisteredComponent<any>[] {
    const registeredComponents = (this.componentRegistry[placeholder] || []).reduce(
      (result: Record<string, RegisteredComponent<any>>, component: RegisteredComponent<any>) => {
        result[component.name] = component;
        return result;
      },
      {}
    );

    const componentGroupsWithDefinedOrder = (orderedComponents || []).map(componentNameGroup => {
      const componentGroup = componentNameGroup.map(name => {
        const component = { ...registeredComponents[name] };
        delete registeredComponents[name];
        return component;
      });
      return componentGroup;
    });

    const filteredComponents = componentGroupsWithDefinedOrder
      .map(group => {
        const component = this.getFirstEnabledComponentInGroup(
          group,
          useExperiment,
          context,
          parentComponents
        );
        return component;
      })
      .filter(component => !!component) as RegisteredComponent<any>[];

    return filteredComponents;
  }

  private addDataNeeds(componentName: string, dataNeeds?: PluginDataNeeds) {
    if (dataNeeds && Object.keys(dataNeeds.contentful).length > 0) {
      for (const dataName of Object.keys(dataNeeds.contentful)) {
        this.dataNeeds.contentful[`${componentName}_${dataName}`] = dataNeeds.contentful[dataName];
      }
    }
  }

  private addStyleNeeds(styleNeeds?: SwanStyleSheetKey[]) {
    if (styleNeeds) {
      this.styleNeeds = [...new Set([...this.styleNeeds, ...styleNeeds])];
    }
  }

  private getFirstEnabledComponentInGroup(
    components: RegisteredComponent<any>[],
    useExperiment: (context: PluginExperimentContext) => string | undefined,
    context: PluginEnableContext,
    parentComponents: string[]
  ) {
    return components.find(
      component =>
        component.enable(context, parentComponents) &&
        (!component.experimentContext || useExperiment(component.experimentContext))
    );
  }
}
